import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Extract a user-friendly error message from an unknown error.
 * Prefers API response message/error (top-level or nested in data), then Error.message, then fallback.
 */
export function getErrorMessage(error: unknown, fallback = "Something went wrong"): string {
  if (error == null) return fallback;
  const err = error as {
    response?: { data?: { message?: string; error?: string; data?: { message?: string; error?: string } } };
    message?: string;
  };
  // API response: message/error at top-level or in data
  const data = err.response?.data;
  const apiMessage =
    data?.message ?? data?.error ?? data?.data?.message ?? data?.data?.error;
  if (apiMessage && typeof apiMessage === "string") return apiMessage;
  // Error.message (e.g. set by axios interceptor or thrown by api client)
  if (error instanceof Error && error.message) return error.message;
  if (typeof err.message === "string" && err.message) return err.message;
  return fallback;
}

/** Login path for redirects. Uses Vite base so it works in production when app is under a subpath. */
export function getLoginUrl(): string {
  const base =
    typeof (import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL === "string"
      ? (import.meta as { env?: { BASE_URL?: string } }).env.BASE_URL
      : "/";
  const baseNorm = base.endsWith("/") ? base.slice(0, -1) : base;
  return `${baseNorm}/admin/login`;
}

/**
 * Get the URL to use for viewing/downloading an attachment.
 * Uses only signed download_url or an absolute http(s) URL. Never use internal paths
 * (e.g. uploads/internal/...) as href, or in production the link becomes /track/uploads/... and 404s.
 */
export function getAttachmentViewUrl(attachment: {
  download_url?: string | null;
  downloadUrl?: string | null;
  file_url?: string;
  fileUrl?: string;
}): string {
  const download =
    attachment.download_url ?? attachment.downloadUrl ?? null;
  const file = attachment.file_url ?? attachment.fileUrl ?? null;
  if (download && typeof download === "string" && download.trim()) return download.trim();
  if (file && typeof file === "string" && file.trim().toLowerCase().startsWith("http")) return file.trim();
  return "";
}

/** Get display name for an attachment (file_name or fileName). */
export function getAttachmentFileName(attachment: {
  file_name?: string;
  fileName?: string;
}): string {
  return attachment.file_name ?? attachment.fileName ?? "File";
}

/** Get file size in bytes if present (file_size or fileSize). */
export function getAttachmentFileSize(attachment: {
  file_size?: number | null;
  fileSize?: number | null;
}): number | null {
  const n = attachment.file_size ?? attachment.fileSize;
  return n != null ? n : null;
}

/** True if current page is the login (or auth) page, so redirect can be skipped to avoid loops. */
export function isOnLoginPage(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  return path.includes("/admin/login") || path.endsWith("/login");
}
